import React, { useEffect, useState } from 'react';
import {
  IFabricationMockup,
  IPavementOrder
} from '../../../../../store/ducks/fabrication-mockups/types';
import moment from 'moment';
import ViewOptionsButton from './ViewOptionsButton';
import theme from '../../../../../assets/styles/theme/theme';
import { Container } from './styles';
import Table from '../../../../../components/Table';

interface IMockupsTableDataSource {
  key: number;
  mockups: string;
  orderQuantities: number;
  numberOfPavements: number;
  associatedProjects: number;
  deliveredVolume: number;
  lastUpdated: string;
  options: IFabricationMockup;
}

interface IMockupsTableProps {
  contractNumber: string;
  filteredMockups: IFabricationMockup[];
  searchTerm: string;
}

const MockupsTableFacelift: React.FC<IMockupsTableProps> = ({
  contractNumber,
  filteredMockups,
  searchTerm
}) => {
  const [mockupsData, setMockupsData] = useState<IMockupsTableDataSource[]>();

  const pageSizeOptions =
    window.innerWidth <= Number(theme.breakpoints.tablet.replace('px', ''))
      ? [5, 10, 20]
      : [10, 20, 50];

  const getColumns = () => {
    const getTextWithSearchTerm = (text: string): React.ReactNode => {
      if (text.toUpperCase().includes(searchTerm.toUpperCase())) {
        const startPos = text.toUpperCase().indexOf(searchTerm.toUpperCase());
        return (
          <>
            {text.substring(0, startPos)}
            <strong>
              {text.substring(startPos, startPos + searchTerm.length)}
            </strong>
            {text.substring(
              startPos + searchTerm.length,
              startPos + text.length
            )}
          </>
        );
      } else {
        return <>{text}</>;
      }
    };

    const columns = [
      {
        title: 'Maquetes',
        dataIndex: 'mockups',
        width: '6rem',
        sorter: (a: IMockupsTableDataSource, b: IMockupsTableDataSource) =>
          Number(a.mockups.replace('-', '')) -
          Number(b.mockups.replace('-', '')),
        render: (text: string) => <>{getTextWithSearchTerm(text)}</>
      },
      {
        title: 'Nº Pedidos',
        dataIndex: 'orderQuantities',
        width: '6.5rem',
        sorter: (a: IMockupsTableDataSource, b: IMockupsTableDataSource) =>
          Number(a.orderQuantities) - Number(b.orderQuantities),
        render: (orderQuantities: number) => (
          <>{getTextWithSearchTerm(orderQuantities.toString())}</>
        )
      },
      {
        title: 'Pavimentos',
        dataIndex: 'numberOfPavements',
        width: '6rem',
        sorter: (a: IMockupsTableDataSource, b: IMockupsTableDataSource) =>
          a.numberOfPavements - b.numberOfPavements,
        render: (numberOfPavements: number) => (
          <>{getTextWithSearchTerm(numberOfPavements.toString())}</>
        )
      },
      {
        title: 'Projetos associados',
        dataIndex: 'associatedProjects',
        width: '7rem',
        sorter: (a: IMockupsTableDataSource, b: IMockupsTableDataSource) =>
          a.associatedProjects - b.associatedProjects,
        render: (associatedProjects: number) => (
          <>{getTextWithSearchTerm(associatedProjects.toString())}</>
        )
      },
      {
        title: 'Peso total',
        dataIndex: 'deliveredVolume',
        width: '7.5rem',
        sorter: (a: IMockupsTableDataSource, b: IMockupsTableDataSource) =>
          Number(a.deliveredVolume) - Number(b.deliveredVolume),
        render: (deliveredVolume: number) => (
          <>{getTextWithSearchTerm(`${deliveredVolume} T`)}</>
        )
      },
      {
        title: 'Última atualização',
        dataIndex: 'lastUpdated',
        width: '7rem',
        sorter: (a: IMockupsTableDataSource, b: IMockupsTableDataSource) =>
          moment(a.lastUpdated).toDate().getTime() -
          moment(b.lastUpdated).toDate().getTime(),
        render: (lastUpdated: string) => (
          <>
            {lastUpdated
              ? getTextWithSearchTerm(
                  moment(lastUpdated).format('DD/MM/YYYY, hh:mm')
                )
              : ' - '}
          </>
        )
      },
      {
        title: '',
        dataIndex: 'options',
        width: '1%',
        render: (mockup: IFabricationMockup) => (
          <>
            <ViewOptionsButton
              contractNumber={contractNumber}
              mockup={mockup}
              mockups={filteredMockups}
            />
          </>
        )
      }
    ];
    return columns;
  };

  useEffect(() => {
    const data: IMockupsTableDataSource[] = filteredMockups.map(
      (mockup, idx) => {
        const getQuantities = (): {
          associatedProjects: number;
          orderQuantities: number;
          deliveredVolume: number;
        } => {
          const pavementProjectsIds: number[] = [];
          const orders: IPavementOrder[] = [];

          mockup.pavements.forEach((pav) => {
            pav.projects?.forEach((proj) => {
              if (!pavementProjectsIds.includes(proj.id)) {
                pavementProjectsIds.push(proj.id);
              }
            });

            pav.orders?.forEach((order) => {
              if (!orders.find((or) => order.id === or.id)) {
                orders.push(order);
              }
            });
          });

          const deliveredVolume = orders.reduce(
            (accVolume, currOrder) => accVolume + currOrder.totalWeight,
            0
          );

          return {
            associatedProjects: pavementProjectsIds.length,
            orderQuantities: orders.length,
            deliveredVolume
          };
        };

        const { orderQuantities, associatedProjects, deliveredVolume } =
          getQuantities();

        return {
          key: idx,
          mockups: mockup.name,
          orderQuantities,
          numberOfPavements: mockup.pavements.length || 0,
          associatedProjects,
          deliveredVolume,
          lastUpdated: mockup.pavements[0].updatedAt || '',
          options: mockup
        };
      }
    );

    setMockupsData(data);
  }, [filteredMockups]);

  return (
    <Container>
      <Table
        columns={getColumns()}
        data={mockupsData || []}
        pageSizeOptions={pageSizeOptions}
        hasPagination
      />
    </Container>
  );
};
export default MockupsTableFacelift;
